import { apiClient, type PaginatedResponse } from '../../../api/client'
import { type PaginationOptions } from '../../../api/pagination'
import { SuppliersuppliersApi } from '../../../generated-types/apis/SuppliersuppliersApi'
import { Configuration } from '../../../generated-types/runtime'
import Cookies from 'js-cookie'
import LogTool from '../../../logger/logTools'
import { createContact, getPKfromSelf } from '../../../utils/functions'
import { createArticle } from '../../Item/utils/functions'
import { type Supplier, type SupplierInfo } from '../utils/types'
import { APISupplierFromJSON } from '../../../generated-types/models/APISupplier'

const log = new LogTool({ context: 'SupplierAPI', enable: true, logLevel: 'warn' })

/**
 * Get API configuration with authentication token
 */
function getApiConfiguration(): Configuration {
  const envApiUrl = process.env.REACT_APP_API_URL
  if (!envApiUrl) {
    throw new Error('API URL is not defined. Please set REACT_APP_API_URL environment variable.')
  }
  
  return new Configuration({
    basePath: envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl,
    accessToken: () => {
      const token = Cookies.get('access')
      return token || ''
    }
  })
}

/**
 * Get API instance (lazy initialization for test compatibility)
 */
function getSuppliersApi(): SuppliersuppliersApi {
  return new SuppliersuppliersApi(getApiConfiguration())
}

/**
 * Create SupplierInfo from API response
 */
export function createSupplierInfo(supplierResponse: any): SupplierInfo {
  const apiSupplier = APISupplierFromJSON(supplierResponse)

  return {
    ...apiSupplier,
    ...(supplierResponse.contacts && Array.isArray(supplierResponse.contacts)
      ? { contacts: supplierResponse.contacts }
      : { contacts: [] }),
    key: getPKfromSelf(apiSupplier.self),
  }
}

/**
 * Create Supplier from API response
 */
export function createSupplier(supplierResponse: any): Supplier {
  const parseSupplierContacts = (contactResponse: any) => {
    if (Array.isArray(contactResponse)) {
      if (contactResponse.every(contact => typeof contact === 'object')) {
        return contactResponse.map(contact => createContact(contact))
      }
    }
    return contactResponse
  }

  const parseSupplierArticles = (articleResponse: any) => {
    if (Array.isArray(articleResponse)) {
      if (articleResponse.every(article => typeof article === 'object')) {
        return articleResponse.map(article => createArticle(article))
      }
    }
    return articleResponse
  }

  const apiSupplier = APISupplierFromJSON(supplierResponse)

  return {
    ...apiSupplier,
    key: getPKfromSelf(apiSupplier.self),
    contacts: parseSupplierContacts(apiSupplier.contacts),
    articles: parseSupplierArticles(apiSupplier.articles),
  }
}

/**
 * Convert form input to API JSON
 */
export function formInputToAPISupplierJSON(input: any): any {
  return {
    ...(input.name && { name: input.name }),
    ...(input.ustId && { ust_id: input.ustId }),
    ...(input.versionComment && { version_comment: input.versionComment }),
    contacts:
      input.contacts && Array.isArray(input.contacts) && input.contacts.length > 0
        ? typeof input.contacts[0] === 'string'
          ? input.contacts
          : input.contacts.map((contact: any) => contact.self)
        : [],
  }
}

/**
 * Fetch paginated suppliers from API
 */
export async function fetchSuppliers(
  options: PaginationOptions = {}
): Promise<PaginatedResponse<Supplier>> {
  log.info('Fetching suppliers with options:', options)

  const response = await getSuppliersApi().v1SupplierSuppliersList({
    page: options.page,
    pageSize: options.pageSize,
  })

  const suppliers = response.results ? response.results.map(createSupplier) : []

  return {
    count: response.count || 0,
    next: response.next || null,
    previous: response.previous || null,
    results: suppliers,
  }
}

/**
 * Fetch a single supplier by ID or URL
 */
export async function fetchSupplier(
  idOrUrl: string | number,
  expand?: string[]
): Promise<Supplier> {
  log.info('Fetching supplier:', idOrUrl)

  const url = typeof idOrUrl === 'number' || !idOrUrl.includes('/')
    ? `v1/supplier/suppliers/${idOrUrl}/`
    : idOrUrl

  const params = expand ? [`expand=${expand.join(',')}`] : []
  const fetchUrl = params.length > 0 ? `${url}?${params.join('&')}` : url

  const response = await apiClient.get<any>(fetchUrl)
  return createSupplier(response)
}

/**
 * Create a new supplier
 */
export async function createSupplierApi(
  supplierData: any
): Promise<Supplier> {
  log.info('Creating supplier:', supplierData)

  const json = formInputToAPISupplierJSON(supplierData)
  const response = await getSuppliersApi().v1SupplierSuppliersCreate({
    aPISupplierRequest: json,
  })

  return fetchSupplier(response.self, ['contacts', 'articles'])
}

/**
 * Update an existing supplier
 */
export async function updateSupplierApi(
  idOrSelf: string | number,
  supplierData: any
): Promise<Supplier> {
  log.info('Updating supplier:', idOrSelf, supplierData)

  const json = formInputToAPISupplierJSON(supplierData)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    const response = await getSuppliersApi().v1SupplierSuppliersUpdate({
      id: idOrSelf,
      aPISupplierRequest: json,
    })
    return createSupplier(response)
  }

  // For URL-based updates, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/supplier/suppliers/${idOrSelf}/`
  const response = await apiClient.put<any>(url, json)
  return createSupplier(response)
}

/**
 * Patch an existing supplier (partial update)
 */
export async function patchSupplierApi(
  idOrSelf: string | number,
  supplierData: Partial<any>
): Promise<Supplier> {
  log.info('Patching supplier:', idOrSelf, supplierData)

  const json = formInputToAPISupplierJSON(supplierData)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    const response = await getSuppliersApi().v1SupplierSuppliersPartialUpdate({
      id: idOrSelf,
      aPIPatchedSupplierRequest: json,
    })
    return createSupplier(response)
  }

  // For URL-based updates, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/supplier/suppliers/${idOrSelf}/`
  const response = await apiClient.patch<any>(url, json)
  return createSupplier(response)
}

/**
 * Delete a supplier
 */
export async function deleteSupplierApi(idOrSelf: string | number): Promise<void> {
  log.info('Deleting supplier:', idOrSelf)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    await getSuppliersApi().v1SupplierSuppliersDestroy({ id: idOrSelf })
    return
  }

  // For URL-based deletes, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/supplier/suppliers/${idOrSelf}/`
  await apiClient.delete(url)
}

/**
 * Delete multiple suppliers
 */
export async function deleteSuppliersApi(suppliers: Supplier[]): Promise<void> {
  log.info('Deleting suppliers:', suppliers.length)

  const deletePromises = suppliers.map(supplier => 
    supplier.self ? deleteSupplierApi(supplier.self) : Promise.resolve()
  )
  await Promise.all(deletePromises)
}

/**
 * Add a contact to a supplier
 */
export async function addContactToSupplier(
  supplierIdOrSelf: string | number,
  contactData: any
): Promise<void> {
  log.info('Adding contact to supplier:', supplierIdOrSelf)

  const supplierId = typeof supplierIdOrSelf === 'string' && supplierIdOrSelf.includes('/')
    ? getPKfromSelf(supplierIdOrSelf)
    : supplierIdOrSelf

  await apiClient.post(`v1/supplier/suppliers/${supplierId}/contacts/`, [contactData])
}

/**
 * Fetch all suppliers (recursively fetches all pages)
 * Note: Use with caution for large datasets
 */
export async function fetchAllSuppliers(
  options: Omit<PaginationOptions, 'page'> = {}
): Promise<Supplier[]> {
  log.info('Fetching all suppliers')

  let allSuppliers: Supplier[] = []
  let page = 1
  let hasMore = true

  while (hasMore) {
    const response = await fetchSuppliers({ ...options, page })
    allSuppliers = [...allSuppliers, ...response.results]
    
    hasMore = response.next !== null
    page++
  }

  log.info(`Fetched ${allSuppliers.length} suppliers total`)
  return allSuppliers
}

/**
 * Fetch suppliers as options for select inputs (first page)
 * Returns options in the format: { value: string, label: string }
 */
export async function fetchSupplierOptions(): Promise<
  { value: string; label?: string; disabled?: boolean }[]
> {
  log.info('Fetching supplier options')

  const response = await fetchSuppliers({ page: 1 })
  
  return response.results
    .filter(supplier => supplier.self !== undefined)
    .map(supplier => ({
      value: supplier.self!,
      label: `${supplier.name ?? ''}`,
    }))
}

/**
 * Fetch all suppliers as options for select inputs (all pages)
 * Returns options in the format: { value: string, label: string }
 * Note: Use with caution for large datasets
 */
export async function fetchAllSupplierOptions(): Promise<
  { value: string; label?: string; disabled?: boolean }[]
> {
  log.info('Fetching all supplier options')

  const allSuppliers = await fetchAllSuppliers()
  
  return allSuppliers
    .filter(supplier => supplier.self !== undefined)
    .map(supplier => ({
      value: supplier.self!,
      label: `${supplier.name ?? ''}`,
    }))
}

